'use client';

import type React from 'react';
import { IconShare } from '@repo/svgs';

type SharePayload = {
  title?: string;
  text?: string;
  url?: string;
};

export interface ShareButtonProps extends SharePayload {
  className?: string;
  ariaLabel?: string;
  disabled?: boolean;
  icon?: React.ReactNode;
  onShared?: () => void;
  onCopied?: () => void;
}

async function copyToClipboard(text: string) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export function ShareButton({
  title,
  text,
  url,
  className,
  ariaLabel = 'Share',
  disabled,
  icon,
  onShared,
  onCopied,
}: ShareButtonProps) {
  const handleShare = async () => {
    const resolvedUrl = url ?? window.location.href;

    if (navigator.share) {
      try {
        const shareData: ShareData = { url: resolvedUrl };
        if (title) shareData.title = title;
        if (text) shareData.text = text;
        await navigator.share(shareData);
        onShared?.();
        return;
      } catch {
        // user cancelled / platform failure -> fallback to copy
      }
    }

    const copied = await copyToClipboard(resolvedUrl);
    if (copied) {
      onCopied?.();
      return;
    }
  };

  return (
    <button
      type="button"
      className={className}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={disabled ? undefined : handleShare}
    >
      {icon ?? <IconShare className="w-4 h-4 icon-primary" />}
    </button>
  );
}
